import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Handshake, Percent, GraduationCap, Headset, Globe2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Reveal, SectionHeader } from "../components/Shared";
import { Seo } from "../components/Seo";
import { api, errorMessage } from "../api";

const PARTNER_TYPES = ["Reseller", "Referral partner", "Implementation / integration partner", "Technology partner"];
const COUNTRIES = ["United Arab Emirates", "Saudi Arabia", "Qatar", "Oman", "Kuwait", "Bahrain", "Egypt", "Jordan", "India", "Pakistan", "Kenya", "Nigeria", "South Africa", "United Kingdom", "United States", "Other"];

const BENEFITS = [
  { icon: Percent, title: "Recurring margins", text: "Earn on every subscription you bring in — for as long as the customer stays." },
  { icon: GraduationCap, title: "Sales & product enablement", text: "Demo environment, pitch decks, pricing guidance and certification for your team." },
  { icon: Headset, title: "Deal support", text: "Fleetroot specialists join your discovery calls, demos and implementations." },
  { icon: Globe2, title: "Territory focus", text: "Priority lead routing in your country and co-marketing in local languages." },
];

const STEPS = ["Apply with the form", "Intro call within 5 working days", "Sign the partner agreement", "Onboarding, demo account & first deals"];

const initial = { company: "", name: "", email: "", phone: "", country: "", website: "", partnerType: "", message: "" };

const Partners = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country || !form.partnerType) {
      toast.error("Please choose your country and partner type");
      return;
    }
    setLoading(true);
    try {
      await api.post("/partner-applications", form);
      setDone(true);
    } catch (err) {
      toast.error(errorMessage(err, "Could not submit. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Partner program — Resell Fleetroot in your market" description="Become a Fleetroot reseller or implementation partner. Recurring margins, enablement, deal support and territory focus across the GCC and beyond." />
      <section className="pt-16 pb-12 md:pt-24 md:pb-16">
        <div className="container-x grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
          <div>
            <span className="eyebrow-pill fade-up">Partner program</span>
            <h1 className="h-display mt-6 fade-up delay-1">Grow with Fleetroot in your market.</h1>
            <p className="mt-6 max-w-xl text-lg md:text-xl text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">
              We're expanding across the GCC and beyond through resellers and implementation partners who know their local logistics landscape. Bring Fleetroot to your customers and build a recurring revenue line.
            </p>
            <ul className="mt-8 space-y-3 fade-up delay-3">
              {["Fleet management, delivery management and driver app in one platform", "Proven with pharmacies, retailers, QSRs and 3PLs", "Fast to implement — most customers live in days"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px]">
                  <CheckCircle2 size={17} className="text-[var(--fr-primary)] mt-0.5 flex-shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <Reveal className="card p-6 md:p-8" data-testid="partner-form-card">
            {done ? (
              <div className="text-center py-10" data-testid="partner-form-success">
                <span className="w-14 h-14 rounded-full bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center mx-auto">
                  <Handshake size={24} />
                </span>
                <h3 className="font-display font-bold text-2xl mt-5">Application received</h3>
                <p className="mt-2 text-[var(--fr-ink-2)]">Thanks, {form.name.split(" ")[0]}. Our partnerships team will reach out within 5 working days.</p>
                <Link to="/" className="btn btn-outline mt-6">Back to home</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="partner-form">
                <h3 className="font-display font-bold text-xl">Apply to become a partner</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block text-sm font-medium">Company<input className="fr-input mt-1.5" required value={form.company} onChange={set("company")} data-testid="partner-company" /></label>
                  <label className="block text-sm font-medium">Your name<input className="fr-input mt-1.5" required value={form.name} onChange={set("name")} data-testid="partner-name" /></label>
                  <label className="block text-sm font-medium">Work email<input className="fr-input mt-1.5" type="email" required value={form.email} onChange={set("email")} data-testid="partner-email" /></label>
                  <label className="block text-sm font-medium">Phone<input className="fr-input mt-1.5" value={form.phone} onChange={set("phone")} placeholder="+971 …" data-testid="partner-phone" /></label>
                  <label className="block text-sm font-medium">
                    Country
                    <Select value={form.country} onValueChange={set("country")}>
                      <SelectTrigger className="fr-input !h-[46px] rounded-xl mt-1.5" data-testid="partner-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </label>
                  <label className="block text-sm font-medium">
                    Partner type
                    <Select value={form.partnerType} onValueChange={set("partnerType")}>
                      <SelectTrigger className="fr-input !h-[46px] rounded-xl mt-1.5" data-testid="partner-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>{PARTNER_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </label>
                </div>
                <label className="block text-sm font-medium">Website<input className="fr-input mt-1.5" placeholder="https://" value={form.website} onChange={set("website")} data-testid="partner-website" /></label>
                <label className="block text-sm font-medium">Tell us about your business<textarea className="fr-input mt-1.5" rows={3} placeholder="Customers you serve, products you already resell, team size…" value={form.message} onChange={set("message")} data-testid="partner-message" /></label>
                <button type="submit" disabled={loading} className="btn btn-primary btn-lg w-full disabled:opacity-60" data-testid="partner-submit">
                  {loading ? "Submitting…" : "Submit application"} <ArrowRight size={17} className="ml-1" />
                </button>
                <p className="text-xs text-[var(--fr-muted)] text-center">We reply within 5 working days. No obligation.</p>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      <section className="section bg-[var(--fr-bg-alt)]" data-testid="partner-benefits">
        <div className="container-x">
          <SectionHeader eyebrow="Why partner" title="What you get as a Fleetroot partner" />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 70} className="card card-hover p-6">
                <span className="w-11 h-11 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center"><Icon size={19} /></span>
                <h3 className="font-display font-bold text-lg mt-5">{title}</h3>
                <p className="mt-2 text-[15px] text-[var(--fr-ink-2)] leading-relaxed">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" data-testid="partner-steps">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <SectionHeader eyebrow="How it works" title="From application to first deal in weeks" />
          <Reveal>
            <ol className="grid sm:grid-cols-2 gap-4">
              {STEPS.map((s, i) => (
                <li key={s} className="card p-5 flex items-start gap-4">
                  <span className="font-display font-bold text-2xl text-[var(--fr-primary-dark)]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[15px] font-medium mt-1.5">{s}</span>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-sm text-[var(--fr-muted)]">Questions first? Email <a href="mailto:partner@fleetroot.com" className="font-semibold text-[var(--fr-primary-dark)]" data-testid="partner-email-link">partner@fleetroot.com</a>.</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Partners;
